/**
 * activationSummary.ts — the two surfaces activation puts a human in front of.
 *
 * BEFORE: the confirm. Not "are you sure?" — a proof-read. Every field the hire purchase instrument
 * declares required that this snapshot can source is shown as it will print, with the four money
 * values under the labels the instrument prints them under, so a fat-fingered instalment reads as
 * an absurd line rather than as a right-looking number in a form. The last moment anyone can catch
 * it is the moment before the contract goes live.
 *
 * THE INSTRUMENT IS A SCHEDULE NOW, AND IT STATES ITS MONEY AS PARTICULARS. Numbered clauses 8.1
 * to 8.3 are gone: the restructured hire purchase agreement is a signed Schedule whose money sits
 * in a particulars table under "The Money", with the operative sentences in clause 2 above the
 * signatures and everything else in the separately published Terms. So the badge beside each line
 * is the SCHEDULE'S OWN ROW LABEL rather than a clause number — which is what an admin holding the
 * rendered PDF beside this dialog is actually matching on — and the one figure the Schedule also
 * states in running prose keeps its clause number, because there it genuinely has one.
 *
 * THE MONEY OPENS ON A PAIR, AND THE DIFFERENCE BETWEEN THEM IS THE CHARGE FOR CREDIT. The v1
 * Schedule states a Cash price above the Hire-purchase price because NRCD 292 s.3 wants both, and
 * the Driver then states, in "What the Driver states to be true", that both were disclosed before
 * signing. Neither is derived from the other — `contracts.cash_price` is a stored fact and the
 * resolver refuses to fall back to `sold_at`, because two equal prices are a statement on a signed
 * page that this contract carries no charge for credit. An unrecorded cash price is therefore the
 * one money line on this surface that also stops the document being generated at all, and it reads
 * here as what it is: the destructive `—` every unfilled required field reads as.
 *
 * TWO REQUIRED FIELDS ARE NOT ON THIS SURFACE, AND THE OMISSION IS NAMED RATHER THAN HIDDEN.
 * `vehicle.year_of_manufacture` and `vehicle.colour` are required by the schedule and are not
 * carried on `SetupSnapshot.vehicle`; the document's own merge pre-flight lists them, with a deep
 * link to the vehicle area, which is the surface that has always been authoritative on what a
 * document is still missing. Adding them here means widening the snapshot, not editing a sentence.
 *
 * THE WITNESS'S TWO FIELDS STAY ON THIS SURFACE, UNDER THE INSTRUMENT THAT NOW PRINTS THEM. They
 * left the hire purchase agreement, not the contract: the witness agreement is a gate on activation
 * and it prints their name and phone, so this is still the last chance anybody has to notice that
 * the wrong person has been named. Filing them under the hire purchase agreement's heading would be
 * the false half of the old surface; dropping them would be the useful half.
 *
 * AND THE INSURANCE PREMIUM ADVANCE IS ON IT FOR THE SAME REASON, WHEREVER ONE IS LINKED. It is a
 * gate on activation too [D-B14], and its Schedule states the premium for the year, what the
 * Borrower paid towards it and the amount Primelane therefore advanced, then four figures that are
 * one arithmetic off that last one — the interest, the weekly repayment, the first repayment date
 * and the number of weeks. A borrower reads them as what they owe and when; nobody after this
 * dialog does. The first three joined this surface with [D-B46], which made the amount advanced a
 * subtraction rather than a copy of the premium: a proof-read that showed only the derived four
 * would ask an admin to check an arithmetic whose inputs are on a different page. They are shown
 * only where the contract actually carries an advance, so a contract without one draws no heading
 * at all, exactly as an instrument with nothing to show never has.
 *
 * AND THE LAST THREE OF THOSE FOUR ARE NOT ASKED OF EVERY ADVANCE [D-B57]. A contract whose hire
 * purchase agreement was signed on paper leaves no room for an advance instalment, so a premium
 * financed on it is settled at the end of the contract and its page carries one repayment row where
 * the schedule would have been. The server activates such a contract; a proof-read that went on
 * counting the weekly repayment, the first repayment date and the term would put "3 fields this
 * activation requires are empty" over a live Activate button, which is the failure the paid-in-full
 * limb below was written to stop the first time.
 *
 * AFTER: the receipt. Not a toast. A contract that has just activated is the one event in this
 * journey worth a surface of its own: the contract number, when it happened, every sealed
 * instrument with the signature evidence underneath it, and the first payment the driver now
 * owes. A toast is gone in four seconds and takes the evidence with it.
 *
 * PURE, AND REACT-FREE. Both models are derived from the same `SetupSnapshot` the manifest is
 * derived from, so the confirm cannot show one set of numbers while the rows show another.
 */

import { format, isValid, parseISO } from 'date-fns';

import { businessDay, formatCurrency } from '@/lib/format';
import { INSTRUMENT_TITLES as INSURANCE_INSTRUMENT_TITLES } from '@/lib/agreements/instrument';

import { advanceRepayments, dailyRateText, termWeeks } from './areas/financialsMath';
import type { AgreementDocTypeKey, AgreementFact, SetupSnapshot } from './setupState';
import type { SetupArea } from './steps';

// ─── Shared helpers ──────────────────────────────────────────────────────────

function asDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? date : null;
}

/**
 * The confirm's date shape, on the BUSINESS day — the twin of `setupState`'s `dateText`, and it has
 * to stay the twin: this model and the manifest are derived from the same snapshot so that the
 * confirm cannot show one set of dates while the rows show another. `businessDay` rather than
 * `asDate` for the reason given there — the instants here (`transactions.transaction_at`, the legacy
 * `signed_at`) were being read in the HOST zone, a day early west of Greenwich.
 */
function dateText(value: string | Date | null | undefined): string | null {
    const date = businessDay(value);
    return date ? format(date, 'MMM d, yyyy') : null;
}

function dateTimeText(value: string | Date | null | undefined): string | null {
    const date = asDate(value);
    return date ? `${format(date, 'MMM d, yyyy')} at ${format(date, 'HH:mm')}` : null;
}

function money(amount: number | null | undefined): string | null {
    return amount === null || amount === undefined ? null : formatCurrency(amount);
}

function filled(value: string | null | undefined): string | null {
    return value && value.trim() ? value.trim() : null;
}

/**
 * The deposit the Schedule credits — `0` where no deposit was taken, NULL for a LINKED ZERO.
 *
 * THE TWO CASES SPLIT AT [D-B41] AND THEY USED TO SHARE AN ANSWER. `agreement_merge_data` now
 * resolves `contract.deposit_amount` to '0.00' where the contract carries NO `contract_deposits`
 * row, and keeps returning NULL where rows exist and sum to zero — the data-error signal, deliberate
 * and preserved. So the proof-read prints GH₵0.00 for the first and the destructive `—` for the
 * second, which is exactly what each contract's own Schedule would render.
 *
 * AND THE `0` IS WHY `Deposit` LEFT `summary.missing`. A required field with nothing in it is
 * counted there and the confirm dialog leads with the count, so returning NULL on a contract the
 * server is happy to activate put "1 field this activation requires is empty" above a live button.
 */
function depositTotal(snapshot: SetupSnapshot): number | null {
    if (snapshot.deposits.length === 0) return 0;
    const total = snapshot.deposits.reduce((sum, deposit) => sum + (deposit.amount ?? 0), 0);
    return total > 0 ? total : null;
}

// ─── The confirm surface ─────────────────────────────────────────────────────

export type ClausePart =
    | { kind: 'text'; text: string }
    | { kind: 'value'; text: string }
    /** A required field with nothing in it. Rendered as `—` in the destructive token. */
    | { kind: 'missing'; text: string };

export interface SummaryClause {
    /**
     * How the Schedule labels the line: the particulars row's own label, or the clause number
     * where the figure is stated in running prose. Unique across the list — it is the React key.
     */
    no: string;
    parts: ClausePart[];
    /** The merge fields this clause carries, for the missing count. */
    fields: string[];
}

/**
 * Which instrument declares a fact required — the heading it is proof-read under.
 *
 * ONE BLOCK PER INSTRUMENT THAT GATES ACTIVATION [D-B18]. Before the witness came off the hire
 * purchase agreement every fact on this surface belonged to one document and the grouping would
 * have been decoration; now `witness.full_name` and `witness.phone` are the witness agreement's,
 * the advance's repayment block is the Insurance Premium Advance's, and a proof-read that
 * presented either under the hire purchase agreement would be asserting something the template
 * does not.
 *
 * THE ADVANCE IS LAST BECAUSE IT IS CONDITIONAL. `ActivateGate` walks these keys in order and
 * draws no heading for an instrument with no facts, so a contract with no linked advance renders
 * exactly the two blocks it did before.
 *
 * AND THE FOURTH KEY IS NOT AN INSTRUMENT — IT IS THE GROUP FOR A REQUIRED FACT NO INSTRUMENT
 * PRINTS. The v1 Schedule identifies the Vehicle by make and model, year, colour, registration and
 * condition; there is no odometer row anywhere on it, and `vehicle.mileage` is a merge field of no
 * template. The reading is still required to activate — `sign_contract` refuses on
 * `VEHICLE_MILEAGE_MISSING` — so it stays on the proof-read; what it cannot do is sit under a
 * heading that says an agreement prints it. `handover` is that group, and the heading says what the
 * reading actually is.
 */
export type SummaryInstrument = 'hire_purchase' | 'witness' | 'insurance_loan' | 'handover';

export const INSTRUMENT_HEADINGS: Record<SummaryInstrument, string> = {
    hire_purchase: 'The hire purchase agreement prints',
    witness: 'The witness agreement prints',
    insurance_loan: 'The insurance premium advance prints',
    handover: 'Recorded at handover, and printed by none of them',
};

/**
 * The four headings for THIS contract — the map above, with the insurance one named for whichever
 * instrument the premium year is actually on [D-B45].
 *
 * A HEADING IS A CLAIM ABOUT WHAT A DOCUMENT PRINTS, and the facts under it are different documents'
 * facts. `insurance_loan` carries two published instruments: the Advance prints a repayment
 * schedule, the Insurance Premium Payment Record prints the premium and what the driver paid. A
 * fixed heading over the Record's three facts would say the advance prints them, on the one surface
 * whose whole purpose is proof-reading what is about to be signed.
 *
 * THE MAP ABOVE STAYS EXPORTED AND STAYS THE ADVANCE'S, because `ActivateGate` reads its KEYS for
 * the block order and because that is the fallback an unknown shape gets.
 */
export function instrumentHeadings(paidInFull: boolean): Record<SummaryInstrument, string> {
    if (!paidInFull) return INSTRUMENT_HEADINGS;
    return {
        ...INSTRUMENT_HEADINGS,
        insurance_loan: 'The insurance premium payment record prints',
    };
}

export interface SummaryFact {
    field: string;
    label: string;
    value: string | null;
    area: SetupArea;
    /**
     * The instrument whose required list this field is on — or `handover`, the group for the one
     * required fact no instrument prints.
     */
    instrument: SummaryInstrument;
    /**
     * Filled by the document generator rather than by anybody in setup — counted as present, and
     * never listed as something to go and fix.
     */
    derived?: boolean;
}

export interface ContractSummary {
    clauses: SummaryClause[];
    facts: SummaryFact[];
    /** The required fields with nothing in them, in reading order. */
    missing: SummaryFact[];
    /**
     * The heading over each block, for THIS contract [D-B45].
     *
     * ON THE SUMMARY AND NOT READ OFF THE CONSTANT, because the insurance heading depends on which
     * instrument the premium year is on, and only this derivation knows that. `ActivateGate` still
     * walks `INSTRUMENT_HEADINGS`' keys for the block ORDER — that has never depended on the
     * contract — and takes each block's words from here.
     */
    headings: Record<SummaryInstrument, string>;
}

const MISSING = '—';

function part(value: string | null, prefix = '', suffix = ''): ClausePart {
    if (value === null) return { kind: 'missing', text: MISSING };
    return { kind: 'value', text: `${prefix}${value}${suffix}` };
}

/**
 * The contract as the hire purchase SCHEDULE will read it.
 *
 * THE LABELS AND THE WORDING ARE THE SCHEDULE'S OWN — the "The Money" particulars rows for the
 * three figures it states as particulars, and clause 2.3 for the one it states in a sentence.
 * The receipt date and the running balance are gone from this surface because they are gone from
 * the instrument: the Schedule credits a deposit without dating it, and states a Term rather than
 * a balance outstanding. A proof-read may only claim what the page claims.
 */
export function contractSummary(snapshot: SetupSnapshot): ContractSummary {
    const cashPrice = snapshot.financials.cashPrice;
    const price = snapshot.financials.sellingPrice;
    const deposits = depositTotal(snapshot);
    const instalment = snapshot.financials.instalment;
    const rate = snapshot.financials.overdueRate;

    const clauses: SummaryClause[] = [
        {
            /**
             * THE STATUTORY PRICE, SHOWN AS STORED AND NEVER DERIVED. `agreement_merge_data` reads
             * `contracts.cash_price` straight and resolves NULL where it is unset, deliberately
             * refusing the fallback to `sold_at` that suggests itself: the two prices printed equal
             * state, on a page somebody signs, that Primelane charged nothing for the credit. So
             * this line recomputes nothing either — the number an admin recorded, or the `—`.
             */
            no: 'Cash price',
            fields: ['contract.cash_price'],
            parts: [
                part(money(cashPrice)),
                { kind: 'text', text: ' — the price at which the Vehicle could be bought outright for cash.' },
            ],
        },
        {
            /**
             * THE PRICE ROW IS THE HIRE-PURCHASE PRICE, and its sentence is the Schedule's own. The
             * v1 body retired "Vehicle Purchase Price" family-wide, and the tail this line used to
             * carry — "it does not change during the Hire Purchase Period" — moved to the separate
             * `Interest on the Hire-purchase price` row. A proof-read may only claim what the page
             * claims, including about which row says what.
             */
            no: 'Hire-purchase price',
            fields: ['contract.selling_price'],
            parts: [
                part(money(price)),
                { kind: 'text', text: ' — the total payable to complete the purchase of the Vehicle, made up of the Deposit and the Weekly Instalments.' },
            ],
        },
        {
            no: 'Deposit credited',
            fields: ['contract.deposit_amount'],
            parts: [part(money(deposits))],
        },
        {
            /**
             * THE WHEN IS NO LONGER A LITERAL ON THE PAGE, AND THIS LINE STOPPED PRETENDING IT IS.
             * Clause 2.1 now says the Weekly Instalment falls due "on the payment day and by the
             * deadline shown above", and the two grid rows it points at each carry Primelane's
             * power to move them — "or such other day/time as Primelane notifies the Driver in
             * writing under clause 4.2 of the Terms". Monday by noon is what those rows resolve to
             * on every contract today, so it is still the figure to proof-read; stating it as an
             * unqualified promise is what the page no longer does.
             */
            no: 'Weekly Instalment',
            fields: ['contract.weekly_instalment'],
            parts: [
                part(money(instalment?.amount ?? null)),
                { kind: 'text', text: ', on the payment day and by the deadline the Schedule shows — every Monday by 12:00 noon, or such other day and time as Primelane notifies the Driver in writing under clause 4.2 of the Terms — until the Hire-purchase price and every other amount payable under this Agreement has been paid in full.' },
            ],
        },
        {
            /**
             * THE ONE MONEY FIGURE THE SCHEDULE PUTS IN A SENTENCE, and the only place a clause
             * number still belongs on this surface.
             *
             * THE WORDING IS 2.3'S OWN, ANNUAL-BASIS LIMB INCLUDED. The clause states the charge as
             * "calculated on an annual basis and charged daily at … per cent per day on the amount
             * unpaid" — the basis of the calculation is on the page a driver signs, so a proof-read
             * that compressed it to "at the rate of" would be reading back a sentence the document
             * does not carry. The FIGURE is still the daily one and only the daily one; the annual
             * per-cent an admin typed appears nowhere on the instrument.
             */
            no: '2.3',
            fields: ['contract.overdue_interest_rate'],
            parts: [
                { kind: 'text', text: 'Interest is payable on any amount that is not paid when it falls due. It is calculated on an annual basis and charged daily at ' },
                part(dailyRateText(rate), '', ' per cent per day'),
                { kind: 'text', text: ' on the amount unpaid, for each day it remains unpaid.' },
            ],
        },
    ];

    const vehicle = snapshot.vehicle;
    const hpa = snapshot.agreements.find((slot) => slot.docType === 'HIRE_PURCHASE') ?? null;

    /**
     * The advance's repayment block, off the one loan setup binds to [D-B17].
     *
     * `advanceRepayments` IS THE DERIVATION — the interest by subtraction off a total held once,
     * the weekly figure from the LATEST rate row, the first repayment from the EARLIEST start, and
     * the term over the TOTAL repayable. Spelling any of them a second time here is how the dialog
     * and the document come to disagree.
     */
    const loan = snapshot.insurance?.loan ?? null;
    const advance = advanceRepayments(
        loan
            ? {
                principal: loan.amount,
                rate: loan.rate,
                instalments: loan.instalments,
                depositTotal: loan.depositTotal,
            }
            : null,
    );

    /**
     * IS THIS PREMIUM SETTLED AT THE END OF THE CONTRACT? [D-B57]
     *
     * TWO LIMBS, AND THE SERVER'S OWN. `contract_hire_purchase_is_paper` answers the first — a
     * signed paper original with no live hire purchase document — and `buildBoardSlots` already
     * derived that conjunction and hung it on every slot, so this is a read of the board's answer
     * rather than a second one over the same rows. The second limb is the loan carrying no
     * instalment row: an advance that has a weekly recovery is recovered weekly wherever the
     * agreement was signed, and 37 paper-contract advances carry one.
     *
     * AN UNREAD OR EMPTY BOARD ANSWERS NO, which is the direction that asks for a repayment
     * schedule rather than declaring an arrangement nobody established. THE UNREAD HALF IS ASKED
     * FIRST AND EXPLICITLY, because a refused read is not an empty board: `useContractAgreements`
     * mints the slots from whichever reads came back, and `masterOnPaper`'s second limb — no live
     * hire purchase document — is vacuously true over a documents list that failed to arrive. A
     * voided-and-reissued paper contract is exactly the shape that would flip, and it would flip
     * towards declaring the arrangement.
     */
    const hpOnPaper =
        !snapshot.agreementsUnread && snapshot.agreements.some((slot) => slot.masterOnPaper);
    const settledAtEnd =
        !!loan && !advance.paidInFull && hpOnPaper && loan.instalments.length === 0;

    const facts: SummaryFact[] = [
        {
            field: 'document.agreement_date',
            label: 'Agreement date',
            value: dateText(hpa?.legacySignedAt),
            area: 'agreements',
            instrument: 'hire_purchase',
            derived: true,
        },
        { field: 'driver.full_name', label: 'Driver', value: filled(snapshot.driver.fullName), area: 'driver', instrument: 'hire_purchase' },
        { field: 'driver.phone', label: 'Driver phone', value: filled(snapshot.driver.phone), area: 'driver', instrument: 'hire_purchase' },
        { field: 'driver.address', label: 'Driver address', value: filled(snapshot.driver.address), area: 'driver', instrument: 'hire_purchase' },
        {
            field: 'vehicle.model',
            label: 'Vehicle',
            value: filled([vehicle.make, vehicle.model].filter(Boolean).join(' ')),
            area: 'vehicle',
            instrument: 'hire_purchase',
        },
        {
            field: 'vehicle.registration',
            label: 'Registration',
            value: filled(vehicle.registration),
            area: 'vehicle',
            instrument: 'hire_purchase',
        },
        // ── The one required fact no instrument prints ───────────────────────────────────────
        // The v1 Schedule's Vehicle particulars are make and model, year, colour, registration and
        // condition — no odometer row, and `vehicle.mileage` is a merge field of no template. It is
        // still required to activate (`VEHICLE_MILEAGE_MISSING`) and still worth proof-reading: the
        // reading taken at handover is what a dispute over wear, mileage or valuation is argued
        // from later. So it keeps its place on this surface under a heading that claims no print.
        // `{{vehicle.mileage}}` reads `contracts.vehicle_mileage`; the namespace is a lie the help
        // copy does not repeat.
        {
            field: 'vehicle.mileage',
            label: 'Odometer at handover',
            value: vehicle.mileage === null ? null : `${vehicle.mileage.toLocaleString('en-GH')} km`,
            area: 'vehicle',
            instrument: 'handover',
        },
        // ── The witness agreement's own two [D-B18] ──────────────────────────────────────────
        {
            field: 'witness.full_name',
            label: 'Witness',
            value: filled(snapshot.witness?.name),
            area: 'agreements',
            instrument: 'witness',
        },
        {
            field: 'witness.phone',
            label: 'Witness phone',
            value: filled(snapshot.witness?.phone),
            area: 'agreements',
            instrument: 'witness',
        },
        // ── The premium year's own, and only where a premium is recorded [D-B14] ────────────
        // A contract that records no premium has no insurance instrument to generate, so these
        // rows would be fields this activation does not require.
        //
        // WHICH ROWS THEY ARE IS THE INSTRUMENT'S ANSWER [D-B45]. An Insurance Premium Advance
        // prints the money it advanced and a repayment block against it — and an Insurance Premium
        // Payment Record prints none of the block, because nothing is advanced and nothing is
        // repaid. Its three merge fields are the premium, what the driver paid and the day they
        // paid it (`insurance.premium_amount` / `_paid_amount` / `_paid_on`, resolved by
        // `agreement_merge_data`), and listing the advance's schedule here instead would put three
        // permanently unfilled rows in front of an admin as work still to do.
        ...(loan && advance.paidInFull
            ? ([
                {
                    field: 'insurance.premium_amount',
                    label: 'Premium',
                    value: money(loan.amount),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
                {
                    field: 'insurance.premium_paid_amount',
                    label: 'Paid by the driver',
                    // CAPPED AT THE PREMIUM, WHICH IS THE RESOLVER'S OWN `LEAST(SUM(deposits),
                    // il.amount)`: an overpaid year must not print more paid than the premium
                    // stated above it, and this row is proof-read against the document it fills.
                    value: money(
                        loan.amount == null
                            ? loan.depositTotal
                            : Math.min(loan.depositTotal, loan.amount),
                    ),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
                {
                    field: 'insurance.premium_paid_on',
                    label: 'Paid on',
                    value: dateText(loan.depositPaidOn),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
            ] satisfies SummaryFact[])
            : []),
        // THE ADVANCE'S GRID, IN THE SCHEDULE'S OWN READING ORDER [D-B46]. The premium, what the
        // Borrower paid towards it and what Primelane therefore advanced come first, because that
        // is the subtraction the four figures below are all taken off — and because the amount
        // advanced is the row the ruling moved. An admin holding the rendered page beside this
        // dialog matches it top to bottom.
        //
        // THE PREMIUM IS `insurance.premium_amount` AND NOT A SECOND `insurance_loan.` KEY: it is a
        // fact about the policy year, which is why the Record prints the same key. The resolver
        // answers both from `insurance_loans.amount`, and there is one premium either way.
        ...(loan && !advance.paidInFull
            ? ([
                {
                    field: 'insurance.premium_amount',
                    label: 'Premium for the insurance period',
                    value: money(loan.amount),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
                {
                    field: 'insurance_loan.deposit_amount',
                    // A FIGURE AND NEVER A BLANK, WHICH IS THE [D-B41] LESSON APPLIED AGAIN. The
                    // resolver prints GH₵0.00 where the loan carries no deposit row at all, and
                    // 283 of the 308 production insurance loans are that shape — a NULL here would
                    // put "1 field this activation requires is empty" above a live button on every
                    // one of them. (The resolver's other case — rows that exist and sum to nought,
                    // its deliberate data-error signal — is not observable from a snapshot that
                    // carries the SUM rather than the rows; it is named here rather than guessed
                    // at, and production holds 0 loans of that shape.)
                    label: 'Paid by the Borrower',
                    value: money(loan.depositTotal),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
                {
                    field: 'insurance_loan.principal_amount',
                    // THE ROW THE RULING MOVED, AND THE FIRST TIME THIS SURFACE HAS LISTED IT. It
                    // was the premium and is now the premium less the two rows above it, so an
                    // admin proof-reading an advance against a part-paid premium can see the
                    // subtraction rather than being asked to trust it.
                    label: 'Amount advanced',
                    value: money(advance.advanced),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
                {
                    field: 'insurance_loan.interest_amount',
                    label: 'Interest on the advance',
                    value: money(advance.interest),
                    area: 'insurance',
                    instrument: 'insurance_loan',
                },
                // THE REPAYMENT BLOCK, OR THE ONE ROW THAT STANDS IN FOR IT [D-B57]. An advance
                // settled at the end of the contract adds no separate weekly while the vehicle is
                // being paid for, and the instalments the driver goes on paying after that recover
                // it — so its page states the arrangement in fixed wording and carries no weekly
                // figure, no first repayment date and no term. Three permanently unfilled rows here
                // would be three fields this activation does not require, counted on the one
                // surface that says whether the contract can go live.
                ...(settledAtEnd
                    ? ([
                        {
                            // NAMES NO MERGE FIELD, and there is none to name: the page states
                            // this in fixed wording rather than printing a figure anybody fills
                            // in. The key is for this surface alone — the React key and the
                            // missing-count's identity — and it carries a value, so it can never
                            // be counted.
                            field: 'insurance_loan.repayment',
                            label: 'Repayment',
                            // WHAT THE PAGE SAYS, NOT WHAT THE RAIL CALLS IT. The heading above
                            // these facts claims to state what the instrument prints, and the
                            // instrument nowhere prints "Settled at the end of the contract" —
                            // that is the admin's name for the arrangement. The sentence is the
                            // dossier's own proof-read of the same two rows (`insuranceSummary`),
                            // so an admin who reads both surfaces reads one wording.
                            value:
                                'None while the vehicle is being paid for, then the contract ' +
                                'instalment until the advance is repaid.',
                            area: 'insurance',
                            instrument: 'insurance_loan',
                        },
                    ] satisfies SummaryFact[])
                    : ([
                        {
                            field: 'insurance_loan.weekly_instalment',
                            label: 'Weekly repayment',
                            value: money(advance.weekly),
                            area: 'insurance',
                            instrument: 'insurance_loan',
                        },
                        {
                            field: 'insurance_loan.first_instalment_date',
                            label: 'First repayment',
                            value: dateText(advance.firstStartsAt),
                            area: 'insurance',
                            instrument: 'insurance_loan',
                        },
                        {
                            field: 'insurance_loan.term_weeks',
                            // A count, and never spelled with decimals — there is no such thing as
                            // a third of a repayment. The same shape the Term cell on the canvas
                            // takes.
                            label: 'Repayment term',
                            value: advance.term === null ? null : `${advance.term} weeks`,
                            area: 'insurance',
                            instrument: 'insurance_loan',
                        },
                    ] satisfies SummaryFact[])),
            ] satisfies SummaryFact[])
            : []),
    ];

    const missingClauseFields: SummaryFact[] = [];
    const clauseAreas: Record<string, SetupArea> = {
        'contract.cash_price': 'financials',
        'contract.selling_price': 'financials',
        'contract.deposit_amount': 'deposit',
        'contract.weekly_instalment': 'financials',
        'contract.overdue_interest_rate': 'financials',
    };
    const clauseLabels: Record<string, string> = {
        'contract.cash_price': 'Cash price',
        'contract.selling_price': 'Selling price',
        'contract.deposit_amount': 'Deposit',
        'contract.weekly_instalment': 'Weekly instalment',
        'contract.overdue_interest_rate': 'Overdue interest rate',
    };
    for (const clause of clauses) {
        if (!clause.parts.some((piece) => piece.kind === 'missing')) continue;
        for (const field of clause.fields) {
            missingClauseFields.push({
                field,
                label: clauseLabels[field] ?? field,
                value: null,
                area: clauseAreas[field] ?? 'financials',
                // Every money clause on this surface is the hire purchase agreement's own.
                instrument: 'hire_purchase',
            });
        }
    }

    const missing = [
        ...missingClauseFields,
        ...facts.filter((fact) => !fact.derived && fact.value === null),
    ];

    return { clauses, facts, missing, headings: instrumentHeadings(advance.paidInFull) };
}

// ─── The receipt ─────────────────────────────────────────────────────────────

export interface ReceiptInstrument {
    docType: AgreementDocTypeKey;
    title: string;
    /** `Sealed Jul 28, 2026 at 12:09`, or the execution date where no seal is filed. */
    when: string | null;
    /** One line per signature: who, and when they signed. */
    evidence: string[];
}

export interface ActivationReceiptModel {
    title: string;
    /** `Activated Aug 2, 2026 at 14:22 · GR-1234-24` */
    subtitle: string;
    instruments: ReceiptInstrument[];
    firstPayment: { amount: string; due: string | null; note: string | null } | null;
    vehicleLine: string | null;
}

/**
 * What the receipt calls each sealed instrument.
 *
 * THE TWO ADVANCES ARE NAMED AS THEY ARE PUBLISHED, and that is not a style preference: the v1
 * templates are titled `Insurance Premium Advance` and `Operational Expense Advance`, so "insurance
 * loan agreement" and "loan agreement" name no document a driver has ever been handed. A receipt is
 * the record of what was sealed, and it has to be searchable against the PDF beside it. The other
 * three keep their sentence case, where the title on the page and the words for it are the same.
 *
 * `INSURANCE_LOAN`'S ENTRY IS A FALLBACK NOW, NOT THE ANSWER [D-B45]. That type carries two
 * published titles — `insurance_loan` "Insurance Premium Advance" and `insurance_payment`
 * "Insurance Premium Payment Record" — so the receipt reads the slot's INSTRUMENT first and lands
 * here only where a slot carries none. Naming a sealed Record after the advance would be exactly
 * the searchability failure this map was written to prevent.
 */
const INSTRUMENT_TITLES: Record<AgreementDocTypeKey, string> = {
    HIRE_PURCHASE: 'Hire purchase agreement',
    WITNESS: 'Witness agreement',
    DEPOSIT: 'Deposit agreement',
    INSURANCE_LOAN: 'Insurance Premium Advance',
    LOAN: 'Operational Expense Advance',
    // THE PUBLISHED TITLE, ON THIS MAP'S OWN RULE [D-B48]. `partner_vehicle_schedule` v1 is titled
    // "Hire Fleet Management Agreement" (`20260908020000`), and that string — not "Vehicle
    // schedule" — is the H1 on the sealed PDF and the name in the file store. A receipt is the
    // record of what was sealed and has to be searchable against the document beside it, which is
    // the whole reason the two advances are named here as they are published rather than as the
    // rail speaks about them. The rail and the hazard sentences go on saying "vehicle schedule";
    // this line is the one place the page has to match the paper.
    PARTNER_MANAGEMENT: 'Hire Fleet Management Agreement',
};

const PARTY_NAMES: Record<string, string> = {
    COMPANY_REP: 'Primelane',
    WITNESS: 'the witness',
    DRIVER: 'the driver',
    BORROWER: 'the borrower',
    PARTNER: 'the partner',
};

function signerName(party: string, snapshot: SetupSnapshot): string {
    if (party === 'DRIVER' || party === 'BORROWER') return snapshot.driver.fullName || PARTY_NAMES[party];
    if (party === 'WITNESS') return snapshot.witness?.name || PARTY_NAMES.WITNESS;
    return PARTY_NAMES[party] ?? party;
}

/**
 * What the receipt calls THIS sealed slot — its instrument's published title where the type has
 * two, the map's entry where it has one [D-B45].
 *
 * BY INSTRUMENT AND NOT BY DOCUMENT TITLE, deliberately: the receipt is drawn from the snapshot,
 * whose `AgreementFact` carries the instrument the board derived and no title of its own. The two
 * agree — `agreement_create` picked the template from the same two limbs — and where the slot
 * carries no instrument at all the answer is the advance, which is what this map has always said.
 */
function receiptTitle(slot: AgreementFact): string {
    if (slot.docType === 'INSURANCE_LOAN' && slot.instrument) {
        return INSURANCE_INSTRUMENT_TITLES[slot.instrument];
    }
    return INSTRUMENT_TITLES[slot.docType] ?? slot.docType;
}

/** An instrument counts as executed when the server's own gate is satisfied, or the ceremony is. */
function isExecuted(slot: AgreementFact): boolean {
    if (slot.legacySignedAt && slot.legacyUrl && slot.legacyUrl.trim()) return true;
    return slot.document?.status === 'COMPLETED';
}

/**
 * The receipt, from the same snapshot the manifest reads.
 *
 * SIGNATURE EVIDENCE IS WHAT MAKES "SIGNED" FEEL LEGALLY REAL — a name and a timestamp per
 * party, taken from the signature rows themselves rather than from the document's own status,
 * so a paper-recorded original (which has an execution date and no signature rows) prints its
 * date and claims no evidence it does not hold.
 *
 * The first-payment note is arithmetic the driver can check: a term by division, and a final
 * instalment by SUBTRACTION — never by percentage, which is how rounding drift gets published.
 */
export function activationReceipt(
    snapshot: SetupSnapshot,
    activatedAt: string | Date,
): ActivationReceiptModel {
    const executed = snapshot.agreements.filter(isExecuted);

    const instruments: ReceiptInstrument[] = executed.map((slot) => {
        const document = slot.document;
        const sealed = document?.finalizedAt ? dateTimeText(document.finalizedAt) : null;
        const signedOn = dateText(slot.legacySignedAt);
        return {
            docType: slot.docType,
            title: receiptTitle(slot),
            when: sealed ? `sealed ${sealed}` : signedOn ? `executed ${signedOn}` : null,
            evidence: (document?.signatures ?? [])
                .filter((signature) => signature.status === 'SIGNED')
                .map((signature) => {
                    const when = dateTimeText(signature.signedAt);
                    const who = signerName(signature.party, snapshot);
                    return when ? `${who} · signed ${when}` : who;
                }),
        };
    });

    const instalment = snapshot.financials.instalment;
    const amount = instalment?.amount ?? null;
    const price = snapshot.financials.sellingPrice;
    const balance = price === null || price === undefined ? null : price - (depositTotal(snapshot) ?? 0);

    // THE SAME TERM THE SCHEDULE PRINTS. `termWeeks` is the one derivation — floor, a part-week by
    // subtraction, and a half-pesewa dust guard — so the receipt cannot promise a driver a week
    // more or less than the instrument they just signed states.
    const term = termWeeks(balance, amount);
    const note =
        term === null || amount === null || balance === null
            ? null
            : `${term} weekly instalments against a balance of ${formatCurrency(balance)}; ` +
              `the final one is ${formatCurrency(balance - (term - 1) * amount)}.`;

    const vehicle = snapshot.vehicle;
    const vehicleName = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
    const vehicleLine = vehicleName
        ? [
            vehicleName,
            vehicle.registration,
            vehicle.mileage === null ? null : `handed over at ${vehicle.mileage.toLocaleString('en-GH')} km`,
        ]
            .filter(Boolean)
            .join(' · ')
        : null;

    return {
        title: `Contract #${snapshot.contractId} is active`,
        subtitle: [dateTimeText(activatedAt) ? `Activated ${dateTimeText(activatedAt)}` : 'Activated', vehicle.registration]
            .filter(Boolean)
            .join(' · '),
        instruments,
        firstPayment:
            amount === null
                ? null
                : {
                    amount: formatCurrency(amount),
                    due: dateText(instalment?.startsAt),
                    note,
                },
        vehicleLine,
    };
}
